import { IMessage } from '@eventr/common'
import { ColoursObj, MainButton, Typography } from '@eventr/common-storybook'
import { Stack } from '@mui/material'
import { useSignal } from '@preact/signals-react'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { scrollStyle } from '../../../state/rocketchatState/rocketchatSignals'
import { useScrollContext } from '../../../state/scrollState/scrollContext'
import { scrollRef } from '../../../state/scrollState/scrollSignals'
import { NavigationView } from '../../layout/NavigationView'
import { chatStore } from '../chatStore'
import { useRocketChatContext } from '../rocketChatProvider'
import { ChatFooter } from './chatFooter'
import { ChatInfoMenu } from './ChatInfoMenu'
import { ChatMessage } from './chatMessage'
import { ChatNotificationsView } from './ChatNotificationsView'
import { useChatBodyContext } from './chatProvider'
import { ChatTitle } from './chatTitle'
import { ChatUserList } from './ChatUserList'
import { ManageGroupChannel } from './manageGroupChannel'

export const ChatBodyPage = () => {
    const { messages, editMessage, api, messageCount } = useChatBodyContext()
    const { chatView } = useRocketChatContext()
    const { setScrollBehaviour } = useScrollContext()
    const location = useLocation()
    const oldScrollHeight = useSignal<number | undefined>(undefined)

    useEffect(() => {
        if (scrollRef.value.current && scrollStyle.value === 'auto') {
            scrollRef.value.current.scrollTop =
                scrollRef.value.current.scrollHeight -
                scrollRef.value.current.clientHeight
        } else if (
            scrollRef.value.current &&
            scrollStyle.value === 'stationary'
        ) {
            scrollRef.value.current.scrollTop =
                scrollRef.value.current.scrollHeight - oldScrollHeight.value
        }
    }, [messages.value, scrollRef.value])

    function getNumberOfMessagesWithOffset(offset?: number, count?: number) {
        if (scrollRef.value.current) {
            oldScrollHeight.value = scrollRef.value.current.scrollHeight
        }
        api.value
            .getMessages(false, {
                count: count ?? 50,
                offset: offset ?? 0,
            })
            .then((m) => {
                const key = api.peek().getCacheKey('messages')
                chatStore.value?.set(
                    'messages',
                    key,
                    m.messages.concat(messages.value)
                )

                messages.value = m.messages.concat(messages.value)
                messageCount.value = m.total
            })
    }

    function renderMessages(messages: IMessage[]) {
        return messages && messages.length > 0 ? (
            messages.map((message, index) => {
                return (
                    <ChatMessage
                        key={index}
                        message={message}
                        lastMessage={messages[index - 1]}
                        lastMessageUserId={
                            index === 0 ? undefined : messages[index - 1].u._id
                        }
                    />
                )
            })
        ) : (
            <Typography
                paddingTop={'40px'}
                color={ColoursObj.core.primary}
                textAlign={'center'}
            >
                No messages, try sending one!
            </Typography>
        )
    }

    switch (chatView.value) {
        case 'default': {
            return (
                <NavigationView
                    navigationBarTitle={<ChatTitle />}
                    backButtonAction={() => {
                        if (!location.key) {
                            window.pushPath('/tabs/rocketChat')
                        } else {
                            try {
                                window.goBack()
                            } catch {
                                window.pushPath('/tabs/rocketChat')
                            }
                        }
                    }}
                    usePageTransition
                    footer={<ChatFooter />}
                >
                    <Stack maxWidth={'100vw'} boxSizing={'border-box'}>
                        {messageCount.value === undefined && ( // Takes the space of the load more button (for scrolling) until it is determined whether it's needed
                            <div style={{ height: '52px' }} />
                        )}
                        {messages &&
                            messages.value &&
                            messages.value.length < messageCount.value && (
                                <div
                                    style={{
                                        paddingTop: '8px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <MainButton
                                        onClick={() => {
                                            scrollStyle.value = 'stationary'
                                            setScrollBehaviour('initial')
                                            if (
                                                messages.value.length + 50 <=
                                                messageCount.value
                                            ) {
                                                getNumberOfMessagesWithOffset(
                                                    messages.value.length,
                                                    50
                                                )
                                            } else {
                                                getNumberOfMessagesWithOffset(
                                                    messages.value.length,
                                                    messageCount.value -
                                                        messages.value.length
                                                )
                                            }
                                        }}
                                        text="Load more"
                                    />
                                </div>
                            )}
                        {messages && renderMessages(messages.value)}
                    </Stack>
                    {editMessage?.value && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '0',
                                width: '100vw',
                                height: '100%',
                            }}
                        />
                    )}
                </NavigationView>
            )
        }
        case 'chatInfo': {
            return <ChatInfoMenu />
        }
        case 'chatMembers': {
            return <ChatUserList />
        }
        case 'chatNotifications': {
            return <ChatNotificationsView />
        }
        case 'manageGroupChannel': {
            return <ManageGroupChannel />
        }
    }
}
